import { Link, Navigate, useParams } from 'react-router-dom';
import { NotificationCenter } from '@/app/layout/NotificationCenter';
import { useCurrentUser } from '@/app/providers/user';
import { ButtonLink, ValoiVLockup } from '@/shared/ui';
import ShowcaseStylePage from '@/features/landing/pages/ShowcaseStylePage';
import TemplatesPage from '@/features/landing/pages/TemplatesPage';
import { SHOWCASE_STYLES } from '@/features/landing/services/styleSections';

/**
 * Shell for `/showcase/:style` — the rail, the page, and the toast host.
 *
 * The same `.shell` row the landing page and the signed-in app use, so the rail is
 * in the same place at the same width and clicking "See all" changes only the
 * content beside it. No layout component to reuse: `AppLayout` renders an
 * `<Outlet />` and lives inside the guarded group, and this route is public.
 *
 * The bar's CTA depends on who is asking. These pages are public, so a signed-out
 * visitor gets Login -- that is the whole point of reaching them from the landing
 * page. A signed-in one used to get it too, which meant following "See all" from
 * `/home` invited you to log in again on a page you were already authenticated for,
 * with no route back into the app. They now get a link to `/home` instead.
 *
 * Branching the CTA rather than the whole shell: the page itself is identical either
 * way, and these routes stay public so a shared link still works for a stranger.
 */
export default function ShowcaseStyleRoute({ index = false }: { index?: boolean } = {}) {
  const { style = '' } = useParams();
  const { status } = useCurrentUser();
  const signedIn = status === 'authenticated';

  /* An unknown style would render a page titled with whatever was in the URL and
     an empty grid under it. Home instead: there is nothing to show and no useful
     error to state. Skipped for the index, which has no style to validate. */
  if (!index && !SHOWCASE_STYLES.includes(style)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="landing">
      {/* The same bar the landing page carries. These pages are reached from it, and
          without one they had no mark and no way in -- the rail used to supply both.
          On cream rather than over footage here, so the mark keeps its brand green. */}
      <header className="landing__bar landing__bar--solid">
        <div className="landing__bar-inner">
          <Link to="/" className="landing__brand" aria-label="Valoi — home">
            <ValoiVLockup title="Valoi" className="landing__logo" />
          </Link>
          {/* `status === 'authenticated'` rather than a truthy `user`: during the boot
              refresh the status is 'loading' and there is no user yet, so keying on the
              user alone would flash Login at someone who is signed in. Loading falls
              through to Login here, which is the correct guess for a public page. */}
          {signedIn ? (
            <ButtonLink to="/home" variant="secondary">
              Back to Valoi
            </ButtonLink>
          ) : (
            <ButtonLink to="/login" variant="primary">
              Login
            </ButtonLink>
          )}
        </div>
      </header>

      <main>{index ? <TemplatesPage /> : <ShowcaseStylePage />}</main>
      <NotificationCenter />
    </div>
  );
}
